/**
 * Pre-order / Made-to-Order advance payment (International markets)
 *
 * UX flow:
 * - User clicks "Reserve with advance" button on an out-of-stock product
 * - Open the existing bottom contact-details sheet (email + phone)
 * - On "Continue to Checkout", call the made-to-order worker to create the advance payment
 * - Redirect user to the payment URL returned by the worker
 *
 * Note: This file is intentionally UX-only. The backend worker endpoint must be implemented separately.
 */
(function () {
  'use strict';
  // Customer-facing copy comes from window.theme.strings (emitted by
  // snippets/head.liquid from the locale files) so it follows the storefront
  // language. The English argument stays as the fallback for any context where
  // theme.strings has not loaded.
  function themeStr(key, fallback) {
    var s = window.theme && window.theme.strings;
    return (s && s[key]) || fallback;
  }


  // Placeholder endpoint. Replace with your real Cloudflare worker URL when ready.
  const MADE_TO_ORDER_API_URL =
    'https://made-to-order.duavaa-checkout.workers.dev/v1/create-made-to-order-payment';

  // Separate n8n webhook for storing the form data immediately (before payment).
  // TODO: Replace these with your real n8n webhook URL + token.
  const MADE_TO_ORDER_FORM_WEBHOOK_URL =
    'https://n8n-latest-1-js6i.onrender.com/webhook/made-to-order/form-submitted';
  const MADE_TO_ORDER_FORM_WEBHOOK_TOKEN = 'IB98yh97b08G97Vvyf9797V7Vex5s34';

  function generateRandomExpectedRestockDate() {
    const days = 30 + Math.floor(Math.random() * 61); // 30..90 inclusive
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  function showButtonLoading(button) {
    if (!button) return;
    if (button.disabled) return;
    button.disabled = true;
    button.dataset.processing = 'true';

    // Keep styling consistent if the theme has the `.btn__loader` element.
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    if (textWrapper) textWrapper.style.opacity = '0.5';
    if (loader) {
      loader.style.opacity = '1';
      loader.style.display = 'block';
    }
  }

  function hideButtonLoading(button) {
    if (!button) return;
    button.disabled = false;
    button.dataset.processing = 'false';

    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    if (textWrapper) textWrapper.style.opacity = '1';
    if (loader) {
      loader.style.opacity = '0';
      loader.style.display = 'none';
    }
  }

  function resetContactDetailsSubmitButton() {
    const dialog = document.getElementById('contact-details-dialog');
    if (!dialog) return;

    const submitButton = dialog.querySelector('.contact-details__submit');
    if (!submitButton) return;

    submitButton.disabled = false;
    submitButton.classList.remove('is-loading');

    const textWrapper = submitButton.querySelector('.btn__text');
    const loader = submitButton.querySelector('.btn__loader');
    if (textWrapper) textWrapper.style.opacity = '1';
    if (loader) {
      loader.style.opacity = '0';
      loader.style.display = 'none';
    }
  }

  function openContactDetailsSheet() {
    const contactSheet = document.getElementById('contact-details-sheet');
    const dialog = document.getElementById('contact-details-dialog');
    if (!contactSheet || !dialog) throw new Error('Contact details sheet not available');

    if (contactSheet.popupOpen && typeof contactSheet.popupOpen === 'function') {
      contactSheet.popupOpen();
      return;
    }

    if (dialog.showModal && typeof dialog.showModal === 'function') {
      dialog.showModal();
      dialog.removeAttribute('inert');
      dialog.setAttribute('aria-hidden', 'false');
      return;
    }

    dialog.setAttribute('open', '');
    dialog.removeAttribute('inert');
    dialog.setAttribute('aria-hidden', 'false');
  }

  function collectContactDetails() {
    return new Promise((resolve, reject) => {
      const dialog = document.getElementById('contact-details-dialog');
      if (!dialog) {
        resolve({ email: '', phone: '' });
        return;
      }

      let submitted = false;

      const handleCollected = (event) => {
        submitted = true;
        const formData = event && event.detail ? event.detail : {};
        const email = formData.email || '';
        const phone = formData.phone || '';

        cleanup();
        resolve({ email, phone });
      };

      const handleDialogClose = () => {
        // Treat close without submit as cancel.
        if (!submitted) {
          cleanup();
          reject(new Error('User cancelled contact details collection'));
        }
      };

      const cleanup = () => {
        document.removeEventListener('contactDetailsCollected', handleCollected);
        dialog.removeEventListener('close', handleDialogClose);
      };

      document.addEventListener('contactDetailsCollected', handleCollected);
      dialog.addEventListener('close', handleDialogClose);

      // Open after listeners are in place.
      try {
        openContactDetailsSheet();
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
  }

  function getLocaleInfoForPreorder() {
    // Prefer WebhookHelper's market config (already loaded on international markets).
    if (window.WebhookHelper && typeof window.WebhookHelper.getMarketConfig === 'function') {
      const cfg = window.WebhookHelper.getMarketConfig();
      return {
        currency: cfg && cfg.currency ? cfg.currency : 'AED',
        market: cfg && cfg.market ? cfg.market : 'UAE',
        gateway: cfg && cfg.gateway ? cfg.gateway : 'stripe'
      };
    }

    // India doesn't have WebhookHelper market config, so fall back to MarketConfig.
    if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
      const marketInfo = window.MarketConfig.getCurrentMarket();
      const cfg = marketInfo && marketInfo.config ? marketInfo.config : {};
      return {
        currency: cfg && cfg.currency ? cfg.currency : 'INR',
        market: cfg && cfg.market ? cfg.market : 'INR',
        gateway: cfg && cfg.gateway ? cfg.gateway : 'razorpay'
      };
    }

    // Last resort fallback: India.
    return { currency: 'INR', market: 'INR', gateway: 'razorpay' };
  }

  function getDomainExtensionFromPath() {
    const path = window.location.pathname.toLowerCase();
    const match = path.match(
      /^\/(en-(?:ae|sa|gb|sg|us|eu|ca|au)|ar-(?:ae|sa))(?:\/|$)/
    );
    return match && match[1] ? match[1] : '';
  }

  function generateClientEventId() {
    if (window.WebhookHelper && typeof window.WebhookHelper.generateEventId === 'function') {
      return window.WebhookHelper.generateEventId();
    }
    return `mto-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  // The selected paid plating (gold/rose) from the PDP Plating selector, or null
  // for the included Rhodium option. Reads the radio's data-plating-* and the
  // displayed "+price" (already in market units), matching what's charged.
  function getSelectedPlating() {
    var checked = document.querySelector('input[name="plating_option"]:checked');
    if (!checked || checked.getAttribute('data-plating-key') === 'silver') return null;
    var variantId = (checked.getAttribute('data-plating-variant-id') || '').trim();
    if (!variantId) return null;
    var opt = checked.closest('.product__plating__opt');
    var priceEl = opt && opt.querySelector('[data-plating-addon-price]');
    var priceMarket = 0;
    if (priceEl) {
      priceMarket = parseFloat(String(priceEl.textContent || '').replace(/[^0-9.]/g, '')) || 0;
    }
    return {
      variantId: variantId,
      marker: (checked.getAttribute('data-plating-marker') || '').trim(),
      service: (checked.getAttribute('data-plating-service') || 'plating').trim(),
      priceMarket: priceMarket
    };
  }

  function convertPriceCentsToMarketUnits(priceCentsRaw) {
    const priceCents = parseFloat(priceCentsRaw || '0');
    if (!priceCents || Number.isNaN(priceCents) || priceCents <= 0) return 0;

    if (
      window.WebhookHelper &&
      typeof window.WebhookHelper.convertToMarketCurrency === 'function'
    ) {
      const converted = window.WebhookHelper.convertToMarketCurrency(priceCents);
      if (
        window.WebhookHelper.roundUpPrice &&
        typeof window.WebhookHelper.roundUpPrice === 'function'
      ) {
        return window.WebhookHelper.roundUpPrice(converted);
      }
      return Math.ceil(converted);
    }

    if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
      const converted = window.MarketConfig.convertPrice(priceCents);
      return Math.ceil(converted);
    }

    return Math.ceil(priceCents / 100);
  }

  // Fire-and-forget: never block the user flow on this webhook.
  function sendMadeToOrderFormSubmittedWebhook(data) {
    try {
      if (!MADE_TO_ORDER_FORM_WEBHOOK_URL) return;

      const eventPayload = {
        event_name: 'MadeToOrderFormSubmitted',
        event_time: Math.floor(Date.now() / 1000),
        event_id: generateClientEventId(),
        action_source: 'website',

        // Form + product details
        ...data
      };

      fetch(MADE_TO_ORDER_FORM_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Token': MADE_TO_ORDER_FORM_WEBHOOK_TOKEN
        },
        body: JSON.stringify(eventPayload),
        keepalive: true
      }).catch(() => {
        // Silent failure: don't block checkout.
      });
    } catch (e) {
      // Silent failure: don't block checkout.
    }
  }

  async function createMadeToOrderAdvancePayment(payload) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const response = await fetch(MADE_TO_ORDER_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!response.ok) {
        const message = (data && (data.error || data.message)) || text || `HTTP ${response.status}`;
        throw new Error(message || 'Failed to create advance payment');
      }

      return data || {};
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function handlePreorderAdvanceClick(event) {
    const button = event.target.closest('[data-preorder-advance-button]');
    if (!button) return;

    if (button.disabled || button.dataset.processing === 'true') return;
    showButtonLoading(button);

    const expectedRestockDateFromBtn = button.dataset.expectedRestockDate || '';
    const expectedRestockDate = expectedRestockDateFromBtn || generateRandomExpectedRestockDate();

    try {
      const contactDetails = await collectContactDetails();

      const { currency, market, gateway } = getLocaleInfoForPreorder();
      const domainExtension = getDomainExtensionFromPath();

      // Compute FULL-priced unit item price in MARKET currency units (not cents).
      // Worker expects `products[].price` in currency units.
      const variantPriceCentsRaw = button.dataset.variantPriceCents || '0';
      const variantPriceCents = parseFloat(variantPriceCentsRaw);

      let unitPriceMarket = 0;
      if (
        window.WebhookHelper &&
        typeof window.WebhookHelper.convertToMarketCurrency === 'function'
      ) {
        const converted = window.WebhookHelper.convertToMarketCurrency(variantPriceCents);
        if (
          window.WebhookHelper.roundUpPrice &&
          typeof window.WebhookHelper.roundUpPrice === 'function'
        ) {
          unitPriceMarket = window.WebhookHelper.roundUpPrice(converted);
        } else {
          unitPriceMarket = Math.ceil(converted);
        }
      } else if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
        const converted = window.MarketConfig.convertPrice(variantPriceCents);
        unitPriceMarket = Math.ceil(converted);
      } else {
        // Last resort fallback (assume cents represent market units/100).
        unitPriceMarket = Math.ceil(variantPriceCents / 100);
      }

      const quantity = 1;
      const productGid = `gid://shopify/Product/${button.dataset.productId}`;
      const products = [
        {
          product_gid: productGid,
          variant_id: button.dataset.variantId,
          quantity: quantity,
          price: unitPriceMarket
        }
      ];

      // If a paid plating (gold/rose) is selected on the PDP, include it as a
      // separate product line so n8n builds a real plating line item on the
      // final Shopify order. Marker on the main line either way.
      const plating = getSelectedPlating();
      const platingSelected = !!plating;
      if (plating) {
        const mainProps = {};
        if (plating.marker) mainProps[plating.marker] = 'Yes';
        products[0].line_item_properties = mainProps;
        if (plating.variantId && plating.priceMarket > 0) {
          products.push({
            product_gid: '',
            variant_id: plating.variantId,
            quantity: quantity,
            price: plating.priceMarket,
            line_item_properties: { _Service: plating.service }
          });
        }
      }

      const subtotal = products.reduce(
        (sum, p) => sum + ((parseFloat(p.price || 0) || 0) * (parseInt(p.quantity || '0', 10) || 0)),
        0
      );

      const payload = {
        // Product identifiers
        product_id: button.dataset.productId,
        variant_id: button.dataset.variantId,
        product_name: button.dataset.productName || '',
        variant_name: button.dataset.variantName || '',
        quantity: quantity,

        expected_restock_date: expectedRestockDate,

        email: contactDetails.email || '',
        phone: contactDetails.phone || '',

        gateway: gateway || '',
        market,
        currency,
        base_url: window.location.origin,

        // Worker contract (required)
        subtotal,
        products: products,

        // Advance mode (NEW system): international charges 50% of the subtotal.
        // Reverting to the previous theme version drops these two fields, and the worker
        // falls back to the old fixed-advance behavior automatically (no worker change needed).
        advance_mode: 'percentage',
        advance_percent: 50
      };

      // Trigger separate n8n webhook for immediate storage (fire-and-forget).
      // Keep it before calling the payment worker.
      sendMadeToOrderFormSubmittedWebhook({
        product_id: payload.product_id,
        variant_id: payload.variant_id,
        product_name: payload.product_name,
        variant_name: payload.variant_name,
        quantity: payload.quantity,

        expected_restock_date: payload.expected_restock_date,
        email: payload.email,
        phone: payload.phone,
        market: payload.market,
        currency: payload.currency,
        domain_extension: domainExtension,
        base_url: payload.base_url,

        // Include pricing so n8n can display/record what we expect the full order to be.
        unit_price_market: payload.products[0].price,
        subtotal: payload.subtotal,
        plating_selected: platingSelected
      });

      // India (manual fulfillment): redirect user to the pre-order page
      // after contact details are collected. No payment automation, and no
      // made-to-order worker call.
      if (String(currency).toUpperCase() === 'INR') {
        const redirectUrl = new URL('https://duavaa.com/payment/pre-order');
        redirectUrl.searchParams.set('product_id', payload.product_id || '');
        redirectUrl.searchParams.set('variant_id', payload.variant_id || '');
        redirectUrl.searchParams.set('expected_restock_date', payload.expected_restock_date || '');
        redirectUrl.searchParams.set('email', payload.email || '');
        redirectUrl.searchParams.set('phone', payload.phone || '');
        redirectUrl.searchParams.set('currency', payload.currency || 'INR');
        redirectUrl.searchParams.set('market', payload.market || 'INR');

        // Include computed pricing so the team can reference it quickly.
        redirectUrl.searchParams.set('full_subtotal', String(payload.subtotal || '0'));

        // Close sheet right before redirect (prevents overlay confusion).
        if (
          window.ContactDetailsCollector &&
          typeof window.ContactDetailsCollector.closeDialog === 'function'
        ) {
          try {
            window.ContactDetailsCollector.closeDialog();
          } catch (e) {
            // Non-fatal
          }
        }

        window.location.href = redirectUrl.toString();
        return;
      }

      const result = await createMadeToOrderAdvancePayment(payload);

      // Worker contract (flexible for now):
      // - prefer `url`
      // - else `payment_url`
      // - else `redirect_url`
      const redirectUrl = result.url || result.payment_url || result.redirect_url;
      if (!redirectUrl) {
        throw new Error('Payment redirect URL not received from server');
      }

      // Close sheet right before redirect (prevents overlay confusion).
      if (
        window.ContactDetailsCollector &&
        typeof window.ContactDetailsCollector.closeDialog === 'function'
      ) {
        try {
          window.ContactDetailsCollector.closeDialog();
        } catch (e) {
          // Non-fatal
        }
      }

      window.location.href = redirectUrl;
    } catch (err) {
      hideButtonLoading(button);
      resetContactDetailsSubmitButton();

      // If user simply closes/cancels the sheet, don't show an error message.
      const msg = (err && err.message ? String(err.message) : '').toLowerCase();
      if (msg.includes('user cancelled contact details collection') || msg.includes('cancelled contact details')) {
        return;
      }

      // Otherwise show a visible error.
      alert(msg ? err.message : themeStr('preorderStartFailed', 'Unable to start pre-order payment. Please try again.'));
    }
  }

  // --- Keep the MTO advance button in sync with the selected variant ---------
  // The button (and its estimate line) is rendered once server-side; without
  // this, switching variants client-side leaves a stale variant id/price and a
  // stale "ready within X" estimate. Mirrors the liquid bucket thresholds in
  // snippets/product-buttons.liquid.
  function mtoBucketKey(restockDateString) {
    if (!restockDateString) return '30_90';
    var ts = Date.parse(restockDateString);
    if (isNaN(ts)) {
      ts = Date.parse(String(restockDateString).replace(/\//g, '-'));
    }
    if (isNaN(ts)) return '30_90';
    var todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    var daysUntil = Math.floor((ts - todayMidnight.getTime()) / 86400000);
    if (daysUntil <= 10) return '10_15';
    if (daysUntil <= 20) return '10_20';
    if (daysUntil <= 30) return '20_30';
    if (daysUntil <= 45) return '30_45';
    return '30_90';
  }

  function handleVariantChangeForMto(event) {
    var container = event.target;
    var variant = event.detail && event.detail.variant;
    if (!variant || !container || typeof container.querySelectorAll !== 'function') return;

    var blocks = container.querySelectorAll('[data-mto-block]');
    if (!blocks.length) return;

    blocks.forEach(function (blockEl) {
      var map = null;
      var mapEl = blockEl.querySelector('[data-mto-variant-map]');
      if (mapEl) {
        try {
          map = JSON.parse(mapEl.textContent);
        } catch (e) {
          map = null;
        }
      }

      var restock = '';
      if (map) {
        restock = (map.variants && map.variants[String(variant.id)]) || map.defaultRestock || '';
      }

      blockEl.querySelectorAll('[data-preorder-advance-button]').forEach(function (btn) {
        btn.dataset.variantId = String(variant.id);
        btn.dataset.variantPriceCents = String(variant.price);
        btn.dataset.variantName = variant.title || '';
        btn.dataset.expectedRestockDate = restock;
      });

      if (map && map.buckets) {
        var daysText = map.buckets[mtoBucketKey(restock)] || map.buckets['30_90'] || '';
        if (daysText) {
          blockEl.querySelectorAll('[data-mto-days-text]').forEach(function (el) {
            el.textContent = daysText;
          });
        }
      }
    });
  }

  function init() {
    document.addEventListener('click', handlePreorderAdvanceClick, true);
    document.addEventListener('theme:variant:change', handleVariantChangeForMto);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

